import { useQuery, useQueryClient } from '@tanstack/react-query';
import update from 'immutability-helper';
import debounce from 'lodash.debounce';
import maxBy from 'lodash.maxby';
import orderBy from 'lodash.orderby';
import { FC, useContext, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import styled, { css } from 'styled-components';

import FullScreenButton from '@/components/FullScreenButton';
import { SrOnly } from '@/components/Layout/Parts';
import ScrollableTable from '@/components/ScrollableTable';
import {
  ActionsWrap,
  SortableTh,
  Table,
  TableContent,
  Th,
} from '@/components/Table';
import Text from '@/components/Text';
import ViewOptionsModal from '@/components/ViewOptionsModal';
import useDidUpdate from '@/hooks/useDidUpdate';
import OptimisationFinalValueTh from '@/pages/OptimisationOverview/components/OptimisationFinalValueTh';
import {
  OptimisationTestUnitResource,
  OptimisationTestsGroupResource,
  TestsSortState,
} from '@/types/resources';
import { api } from '@/utils/api';
import { GaNetworkType, SortDirection } from '@/utils/enums';
import { RootState } from '@/utils/store';

import { GaNetworkTypeContext } from '../index';
import AvgStatsRow from './AvgStatsRow';
import AvgTipStatsRow from './AvgTipStatsRow';
import DraggableTestRow from './DraggableTestRow';
import GroupControls from './GroupControls';
import { ResultsDescription, ResultsDescriptionSvg } from './TestTable';
import TestTableRow, { Dot } from './TestTableRow';
import TestsBatchFilter from './TestsBatchFilter';

const columns = [
  {
    title: 'Variation Name',
    toggable: false,
    defaultVisibility: true,
  },
  {
    title: 'Views',
    toggable: false,
    defaultVisibility: true,
  },
  {
    title: 'Scene Duration',
    toggable: false,
    defaultVisibility: true,
  },
  {
    title: 'Thumb',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: 'Video Title',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: 'CTR',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: 'Watchtime (s)',
    toggable: true,
    defaultVisibility: false,
  },
  {
    title: 'e30s Reach',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: 'e45s Reach',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: 'e60s Reach',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: 'e80s Reach',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: 'e120s Reach',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: 'e150s Reach',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: 'e4min Reach',
    toggable: true,
    defaultVisibility: false,
  },
  {
    title: 'e8min Reach',
    toggable: true,
    defaultVisibility: false,
  },
  {
    title: '30s Reach',
    toggable: true,
    defaultVisibility: false,
  },
  {
    title: '8min Reach',
    toggable: true,
    defaultVisibility: false,
  },
  {
    title: '25% Reach',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: '50% Reach',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: '75% Reach',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: '100% Reach',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: 'Final Value',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: 'eYTV',
    toggable: true,
    defaultVisibility: false,
  },
  {
    title: 'eYTV 30s',
    toggable: true,
    defaultVisibility: false,
  },
  {
    title: 'eYTV 45s',
    toggable: true,
    defaultVisibility: false,
  },
  {
    title: 'eYTV 60s',
    toggable: true,
    defaultVisibility: false,
  },
  {
    title: 'eYTV 80s',
    toggable: true,
    defaultVisibility: false,
  },
  {
    title: 'eYTV 120s',
    toggable: true,
    defaultVisibility: false,
  },
  {
    title: 'eYTV 150s',
    toggable: true,
    defaultVisibility: false,
  },
  {
    title: 'eYTV 4min',
    toggable: true,
    defaultVisibility: false,
  },
  {
    title: 'Channel',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: 'Audience Channels',
    toggable: true,
    defaultVisibility: false,
  },
  {
    title: 'Audience Segment',
    toggable: true,
    defaultVisibility: false,
  },
  {
    title: 'Max CPV',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: 'Campaign Budget',
    toggable: true,
    defaultVisibility: false,
  },
  // {
  //   title: "Drop Visualization",
  //   toggable: true,
  //   defaultVisibility: true,
  // },
  // {
  //   title: "Drop",
  //   toggable: true,
  //   defaultVisibility: true,
  // },
];

const ControlsHolder = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 12px;
`;

const StyledTable = styled(Table)`
  th,
  td {
    padding: 16px 12px;
    font-size: 11px;
    font-weight: 700;
    vertical-align: top;
    white-space: nowrap;
    &:nth-child(1) {
      min-width: 280px;
      padding-left: 24px;
      white-space: initial;
    }
    &:last-child {
      position: relative;
      z-index: 2;
      width: 48px;
      padding-right: 16px;
    }
  }
`;

interface ResultsDescriptionProps {
  $pushRight: boolean;
}

const StyledResultsDescription = styled(
  ResultsDescription,
)<ResultsDescriptionProps>`
  margin-top: ${({ $pushRight }) => ($pushRight ? 'auto' : 0)};
  margin-right: ${({ $pushRight }) => ($pushRight ? 'auto' : 0)};
`;

const TEST_WINNER_CRITERIA = 'estimatedFinalValue';

export interface HiddeableTest extends OptimisationTestUnitResource {
  is_hidden?: 0 | 1;
}

const GaNetworkSwitch = styled.div`
  height: 32px;
  border: 1px solid ${({ theme }) => theme.colors.lightGray};
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.white};
  padding: 4px;
  display: flex;
  gap: 4px;
`;

const GaNetworkButton = styled(Text)<{ $active: boolean }>`
  border-radius: 4px;
  border: 1px solid transparent;
  color: ${({ theme }) => theme.colors.gray};
  width: 62px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    border-color: ${({ theme }) => theme.colors.lightGray};
  }

  ${({ theme, $active }) =>
    $active &&
    css`
      border-color: ${theme.colors.lightGray};
      color: ${({ theme }) => theme.colors.black};
      background: #f2f2f2;
    `}
`;

interface Props {
  tests?: OptimisationTestUnitResource[];
}

const TipsTable: FC<Props> = ({ tests }) => {
  const { gaNetworkType, setGaNetworkType } = useContext(GaNetworkTypeContext);

  const statsKey =
    gaNetworkType === GaNetworkType.SEARCH ? 'searchStats' : 'watchStats';

  const [activeGroup, setActiveGroup] =
    useState<OptimisationTestsGroupResource | null>(null);

  const [selectedBatchs, setSelectedBatches] = useState<number[]>([]);

  const [areThumbsEnlarged, setAreThumbsEnlarged] = useState(false);

  const tableViewOptions = useSelector(
    (rootState: RootState) =>
      rootState.options.tableViews['optimisationTipsTable'],
  );

  const availableBatches = tests?.reduce(
    (previousValue: number[], currentValue) => {
      return !previousValue.includes(currentValue.batch)
        ? [...previousValue, currentValue.batch]
        : previousValue;
    },
    [],
  );

  const { id: projectId } = useParams();

  const queryClient = useQueryClient();

  const { data: groups } = useQuery(
    ['tips-tests-groups', { projectId }],
    () =>
      api.get<never, OptimisationTestsGroupResource[]>(
        '/v1/optimisation-tests-groups',
        {
          params: {
            optimisation_test_id: projectId,
          },
        },
      ),
    {
      staleTime: 90 * 1000,
    },
  );

  const fullScreenModeEnabled = useSelector(
    (rootState: RootState) => rootState.layout.fullScreenModeEnabled,
  );

  const [sort, setSort] = useState<TestsSortState>({
    direction: SortDirection.DEFAULT,
    property: 'maxCpv',
    type: 'properties',
  });

  const winningTestId = useMemo(
    () => maxBy(tests, (test) => test?.[statsKey]?.[TEST_WINNER_CRITERIA])?.id,
    [tests, statsKey],
  );

  const sortedTests = useMemo(() => {
    const filterTests = (tests?: OptimisationTestUnitResource[]) => {
      if (!selectedBatchs.length) {
        return tests?.filter((test) => test.batch !== 0);
      }
      return tests?.filter((test) => selectedBatchs.includes(test.batch));
    };

    if (sort.direction === SortDirection.DEFAULT) {
      return filterTests(tests);
    }
    const sortType = sort.type === 'properties' ? 'properties' : statsKey;

    const property =
      sort.property as keyof OptimisationTestUnitResource[typeof sortType];

    const direction = sort.direction.toLowerCase() as 'asc' | 'desc';

    return filterTests(
      orderBy(
        tests,
        (test) => {
          if ((test[statsKey]?.views ?? 0) < 50) {
            if (sort.direction === SortDirection.DESC) {
              return -1_000_000_000;
            }
            return 1_000_000_000;
          }
          return sort.type === 'attributes'
            ? parseFloat(test[property])
            : parseFloat(test[sortType]?.[property] ?? '') || -1000;
        },
        direction,
      ),
    );
  }, [sort, tests, selectedBatchs, statsKey]);

  const orderedTests = useMemo<HiddeableTest[]>(() => {
    const filterTests = (tests: HiddeableTest[]) => {
      if (!selectedBatchs.length)
        return tests?.filter((test) => test.batch !== 0);
      return tests.filter((test) => selectedBatchs.includes(test.batch));
    };

    if (!tests) {
      return [];
    }
    const sortType = sort.type === 'properties' ? 'properties' : statsKey;

    const property =
      sort.property as keyof OptimisationTestUnitResource[typeof sortType];

    const direction = sort.direction.toLowerCase() as 'asc' | 'desc';

    if (!activeGroup) {
      if (sort.direction === SortDirection.DEFAULT) {
        return filterTests(tests);
      }

      return filterTests(
        orderBy(
          tests,
          (test) => test[sortType]?.[property] || -1000,
          direction,
        ),
      );
    }

    const testsMap = tests.reduce(
      (acc, curr) => {
        acc[curr.id] = curr;
        return acc;
      },
      {} as { [key: number]: OptimisationTestUnitResource },
    );

    const filteredTests = activeGroup.tests
      .filter((t) => !!testsMap[t.id])
      .map((t) => ({ ...t, ...testsMap[t.id] }));

    if (sort.direction === SortDirection.DEFAULT) {
      return filterTests(filteredTests);
    }

    return filterTests(
      orderBy(
        filteredTests,
        (test) => {
          let sortCriteria =
            sort.type === 'attributes'
              ? test[property]
              : test[sortType]?.[property] || -1000;

          if (
            ((test as HiddeableTest).is_hidden && activeGroup !== null) ||
            (!!(test as HiddeableTest)[statsKey]?.views &&
              ((test as HiddeableTest)[statsKey]?.views ?? 0) < 50)
          ) {
            if (sort.direction === SortDirection.DESC) {
              return -1_000_000_000;
            }
            return 1_000_000_000;
          }

          return sortCriteria || -1000;
        },
        direction,
      ),
    );
  }, [tests, activeGroup, sort, selectedBatchs, statsKey]);

  useDidUpdate(() => {
    const currentGroup = groups?.find((g) => g.id === activeGroup?.id);

    if (!currentGroup) return;

    setActiveGroup(currentGroup);
  }, [groups]);

  const reorderRows = (dragIndex: number, hoverIndex: number) => {
    const reorederedTests = update(activeGroup!.tests, {
      $splice: [
        [dragIndex, 1],
        [
          hoverIndex,
          0,
          activeGroup!.tests![dragIndex] as {
            id: number;
            index: number;
            is_hidden: 0 | 1;
          },
        ],
      ],
    });

    queryClient.setQueryData<OptimisationTestsGroupResource[]>(
      ['tips-tests-groups', { projectId }],
      (groups) => {
        return groups?.map((group) => {
          if (group.id !== activeGroup!.id) return group;

          return {
            ...group,
            tests: reorederedTests,
          };
        });
      },
    );
  };

  const areRowsSortable = !!activeGroup && fullScreenModeEnabled;

  const { current: onDrop } = useRef(
    debounce(async (activeGroup: OptimisationTestsGroupResource) => {
      try {
        await api.put(
          `/v1/optimisation-tests-groups/${activeGroup.id}/reorder`,
          {
            tests: activeGroup.tests.map((t, index) => ({ ...t, index })),
          },
        );
      } catch (err) {
        //
      }
    }, 2000),
  );

  return (
    <div>
      <ControlsHolder>
        {fullScreenModeEnabled && (
          <GroupControls
            groups={groups}
            activeGroup={activeGroup}
            setActiveGroup={setActiveGroup}
            setSelectedBatches={setSelectedBatches}
          />
        )}
        <StyledResultsDescription $pushRight={!fullScreenModeEnabled}>
          <ResultsDescriptionSvg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle cx="8" cy="8" r="8" />
            <path d="M7.33337 10.1568V11.3333H8.66671V10.1568H7.33337ZM7.33337 4.66659V9.37247H8.66671V4.66659H7.33337Z" />
          </ResultsDescriptionSvg>
          <Text $type="smallHeadline">
            <Dot />
            {gaNetworkType === GaNetworkType.SEARCH
              ? 'Youtube Search'
              : 'Youtube Video'}
          </Text>
        </StyledResultsDescription>
        <GaNetworkSwitch>
          <GaNetworkButton
            as="button"
            type="button"
            $type="smallHeadline"
            $active={gaNetworkType === GaNetworkType.WATCH}
            onClick={() => setGaNetworkType!(GaNetworkType.WATCH)}
          >
            YTv
          </GaNetworkButton>
          <GaNetworkButton
            as="button"
            type="button"
            $type="smallHeadline"
            $active={gaNetworkType === GaNetworkType.SEARCH}
            onClick={() => setGaNetworkType!(GaNetworkType.SEARCH)}
          >
            YTs
          </GaNetworkButton>
        </GaNetworkSwitch>
        <ViewOptionsModal columns={columns} tableKey="optimisationTipsTable" />
        <FullScreenButton />
      </ControlsHolder>
      <TestsBatchFilter
        selectedBatchs={selectedBatchs}
        setSelectedBatches={setSelectedBatches}
        availableBatches={availableBatches}
      />
      <ScrollableTable>
        <StyledTable>
          <thead>
            <tr>
              <SortableTh
                title="Name"
                sort={sort}
                setSort={setSort}
                property="name"
                type="attributes"
                disableSort={!!activeGroup}
              />
              <SortableTh
                title="Views"
                sort={sort}
                setSort={setSort}
                property="views"
                disableSort={!!activeGroup}
              />
              {tableViewOptions['Video Title'] && <Th>Video Title</Th>}
              <SortableTh
                title="Scene duration"
                sort={sort}
                setSort={setSort}
                property="sceneDuration"
                type="properties"
                disableSort={!!activeGroup}
              />
              {tableViewOptions['CTR'] && (
                <SortableTh
                  title="CTR"
                  sort={sort}
                  setSort={setSort}
                  property="ctrPercentage"
                  disableSort={!!activeGroup}
                />
              )}
              {tableViewOptions['Watchtime (s)'] && (
                <SortableTh
                  title="Watchtime (s)"
                  sort={sort}
                  setSort={setSort}
                  property="avgWatchtime"
                  disableSort={!!activeGroup}
                />
              )}
              {tableViewOptions['e30s Reach'] && (
                <SortableTh
                  title="e30s Reach"
                  sort={sort}
                  setSort={setSort}
                  property="e30s"
                  disableSort={!!activeGroup}
                />
              )}
              {tableViewOptions['e45s Reach'] && (
                <SortableTh
                  title="e45s Reach"
                  sort={sort}
                  setSort={setSort}
                  property="e45s"
                  disableSort={!!activeGroup}
                />
              )}
              {tableViewOptions['e60s Reach'] && (
                <SortableTh
                  title="e60s Reach"
                  sort={sort}
                  setSort={setSort}
                  property="e60s"
                  disableSort={!!activeGroup}
                />
              )}
              {tableViewOptions['e80s Reach'] && (
                <SortableTh
                  title="e80s Reach"
                  sort={sort}
                  setSort={setSort}
                  property="e80s"
                  disableSort={!!activeGroup}
                />
              )}
              {tableViewOptions['e120s Reach'] && (
                <SortableTh
                  title="e120s Reach"
                  sort={sort}
                  setSort={setSort}
                  property="e120s"
                  disableSort={!!activeGroup}
                />
              )}
              {tableViewOptions['e150s Reach'] && (
                <SortableTh
                  title="e150s Reach"
                  sort={sort}
                  setSort={setSort}
                  property="e150s"
                  disableSort={!!activeGroup}
                />
              )}
              {tableViewOptions['e4min Reach'] && (
                <SortableTh
                  title="e4min Reach"
                  sort={sort}
                  setSort={setSort}
                  property="e240s"
                  disableSort={!!activeGroup}
                />
              )}
              {tableViewOptions['e8min Reach'] && (
                <SortableTh
                  title="e8min Reach"
                  sort={sort}
                  setSort={setSort}
                  property="eMilestoneViewsPercentage"
                  disableSort={!!activeGroup}
                />
              )}
              {tableViewOptions['30s Reach'] && (
                <SortableTh
                  title="30s Reach"
                  sort={sort}
                  setSort={setSort}
                  property="30sViewsPercentage"
                  disableSort={!!activeGroup}
                />
              )}
              {tableViewOptions['8min Reach'] && (
                <SortableTh
                  title={`8min Reach`}
                  sort={sort}
                  setSort={setSort}
                  property="milestoneViewsPercentage"
                />
              )}
              {tableViewOptions['25% Reach'] && (
                <SortableTh
                  title="25% Reach"
                  sort={sort}
                  setSort={setSort}
                  property="p25Rate"
                  disableSort={!!activeGroup}
                />
              )}
              {tableViewOptions['50% Reach'] && (
                <SortableTh
                  title="50% Reach"
                  sort={sort}
                  setSort={setSort}
                  property="p50Rate"
                  disableSort={!!activeGroup}
                />
              )}
              {tableViewOptions['75% Reach'] && (
                <SortableTh
                  title="75% Reach"
                  sort={sort}
                  setSort={setSort}
                  property="p75Rate"
                  disableSort={!!activeGroup}
                />
              )}
              {tableViewOptions['100% Reach'] && (
                <SortableTh
                  title="100% Reach"
                  sort={sort}
                  setSort={setSort}
                  property="p100Rate"
                  disableSort={!!activeGroup}
                />
              )}
              {tableViewOptions['Final Value'] && (
                <OptimisationFinalValueTh
                  {...(!activeGroup && {
                    sort,
                    setSort,
                  })}
                />
              )}
              {tableViewOptions['eYTV'] && (
                <SortableTh
                  title="eYTV"
                  sort={sort}
                  setSort={setSort}
                  property="estimatedFinalValue"
                  disableSort={!!activeGroup}
                />
              )}
              {tableViewOptions['eYTV 30s'] && (
                <SortableTh
                  title="eYTV 30s"
                  sort={sort}
                  setSort={setSort}
                  property="eytv30s"
                  disableSort={!!activeGroup}
                />
              )}
              {tableViewOptions['eYTV 45s'] && (
                <SortableTh
                  title="eYTV 45s"
                  sort={sort}
                  setSort={setSort}
                  property="eytv45s"
                  disableSort={!!activeGroup}
                />
              )}
              {tableViewOptions['eYTV 60s'] && (
                <SortableTh
                  title="eYTV 60s"
                  sort={sort}
                  setSort={setSort}
                  property="eytv60s"
                  disableSort={!!activeGroup}
                />
              )}
              {tableViewOptions['eYTV 80s'] && (
                <SortableTh
                  title="eYTV 80s"
                  sort={sort}
                  setSort={setSort}
                  property="eytv80s"
                  disableSort={!!activeGroup}
                />
              )}
              {tableViewOptions['eYTV 120s'] && (
                <SortableTh
                  title="eYTV 120s"
                  sort={sort}
                  setSort={setSort}
                  property="eytv120s"
                  disableSort={!!activeGroup}
                />
              )}
              {tableViewOptions['eYTV 150s'] && (
                <SortableTh
                  title="eYTV 150s"
                  sort={sort}
                  setSort={setSort}
                  property="eytv150s"
                  disableSort={!!activeGroup}
                />
              )}
              {tableViewOptions['eYTV 4min'] && (
                <SortableTh
                  title="eYTV 4min"
                  sort={sort}
                  setSort={setSort}
                  property="eytv4"
                  disableSort={!!activeGroup}
                />
              )}
              {tableViewOptions['Channel'] && <Th>Channel</Th>}
              {tableViewOptions['Audience Channels'] && (
                <Th>Audience channels</Th>
              )}
              {tableViewOptions['Audience Segment'] && (
                <Th>Audience segment</Th>
              )}
              {tableViewOptions['Max CPV'] && (
                <SortableTh
                  title="Max CPV"
                  sort={sort}
                  setSort={setSort}
                  property="maxCpv"
                  type="properties"
                  disableSort={!!activeGroup}
                />
              )}
              {tableViewOptions['Campaign Budget'] && (
                <SortableTh
                  title="Campaign budget"
                  sort={sort}
                  setSort={setSort}
                  property="campaignBudget"
                  type="properties"
                  disableSort={!!activeGroup}
                />
              )}
              {tableViewOptions['Drop Visualization'] && (
                <Th>Drop Visualization</Th>
              )}
              {tableViewOptions['Drop'] && (
                <Th>
                  Drop
                  <br />
                  <Text as="span" $color="gray" $type="extraSmall">
                    Period: Drop / (s)
                  </Text>
                </Th>
              )}
              <Th>
                <ActionsWrap>
                  <SrOnly>Delete</SrOnly>
                </ActionsWrap>
              </Th>
            </tr>
          </thead>
          <tbody>
            <TableContent
              hasData={!!sortedTests?.length || !!orderedTests?.length}
              isLoading={false}
              isError={false}
              emptyTableText="No tests added"
            >
              {(fullScreenModeEnabled ? orderedTests : sortedTests)?.map(
                (test: HiddeableTest, index) =>
                  areRowsSortable ? (
                    test.is_hidden ? (
                      <TestTableRow
                        key={test.id}
                        test={test}
                        hideThumbnail
                        groupId={activeGroup.id}
                        winning={winningTestId === test.id}
                        areThumbsEnlarged={areThumbsEnlarged}
                        setAreThumbsEnlarged={setAreThumbsEnlarged}
                      />
                    ) : (
                      <DraggableTestRow
                        reorderRows={reorderRows}
                        groupId={activeGroup.id}
                        onDrop={() => onDrop(activeGroup)}
                        index={index}
                        hideThumbnail
                        key={index}
                        test={test}
                        winning={winningTestId === test.id}
                        areThumbsEnlarged={areThumbsEnlarged}
                        setAreThumbsEnlarged={setAreThumbsEnlarged}
                      />
                    )
                  ) : (
                    <TestTableRow
                      key={test.id}
                      test={test}
                      winning={winningTestId === test.id}
                      areThumbsEnlarged={areThumbsEnlarged}
                      setAreThumbsEnlarged={setAreThumbsEnlarged}
                    />
                  ),
              )}
              {areRowsSortable ? (
                <AvgTipStatsRow tests={orderedTests} />
              ) : (
                <AvgStatsRow
                  tests={sortedTests ?? []}
                  tableViewOptionsKey="optimisationTipsTable"
                  statsKey={statsKey}
                />
              )}
            </TableContent>
          </tbody>
        </StyledTable>
      </ScrollableTable>
    </div>
  );
};

export default TipsTable;
